/**
 * Prepped drafts — THE INVERSION MANDATE for the Content Engine.
 *
 * Every cold-start should open on finished work, not a form: this module
 * derives 3–5 ready-to-review draft specs ("Argo prepped these") from REAL
 * records — Company Brain facts, personas, customers, and recent wins —
 * so the landing shows shippable starting points instead of a blank picker.
 *
 * Honesty contract (weekly-update composing discipline):
 *   • every draft is composed ONLY from facts/records that actually exist —
 *     a missing fact means its line (or its whole slot) is omitted, never
 *     invented;
 *   • an empty Brain returns [] — the view falls back to the picker with a
 *     "set up your Brain" hint rather than fabricating context;
 *   • each spec carries `grounding` — the human-readable provenance of the
 *     real facts it was composed from;
 *   • drafts are DRAFTS: the view labels them Argo-prepped, scores them with
 *     the shipped scorer, and publishing stays behind the born-scored gate.
 *
 * PURE by contract: no store, no network, no clock. Deterministic — the same
 * inputs (and the same per-slot `variants` counters) always produce the same
 * specs, so Regenerate is a variant rotation, not a dice roll.
 */

import type {
  BrainFact,
  BrainSection,
  ContentItem,
  ContentItemType,
  Deliverable,
  Person,
} from '../db/types';

// ---------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------

/** A real, already-happened win the drafts can honestly reference. */
export interface RecentWin {
  kind: 'deliverable' | 'closed-won';
  /** What happened, e.g. "Candidate monitoring" or "Bronwick signed". */
  label: string;
  accountName: string;
  /** ISO timestamp/date when known — used only for recency ordering. */
  at?: string;
}

/** Stable slot ids — Regenerate/Dismiss address a slot, not an index. */
export type PreppedSlot =
  | 'customer-win'
  | 'positioning-linkedin'
  | 'x-take'
  | 'icp-pain-blog'
  | 'recovery-story';

/** One prepped draft: everything the gallery card + Open-&-score need. */
export interface PreppedDraftSpec {
  slot: PreppedSlot;
  type: ContentItemType;
  title: string;
  body: string;
  /** Human-readable provenance — the REAL facts this was composed from. */
  grounding: string[];
}

/** templateId prefix stamped on items minted from a prepped draft. */
export const PREPPED_TEMPLATE_PREFIX = 'prepped:';

// ---------------------------------------------------------------------------
// Input derivation helpers
// ---------------------------------------------------------------------------

/**
 * Derive real wins from first-class deliverables (delivered/accepted),
 * customer-embedded delivered deliverables, and closed-won prospects.
 * Sorted most-recent-first where a date exists; dateless wins sink.
 */
export function deriveRecentWins(
  deliverables: Deliverable[],
  people: Person[],
): RecentWin[] {
  const wins: RecentWin[] = [];
  for (const d of deliverables) {
    if (d.status === 'delivered' || d.status === 'accepted') {
      wins.push({
        kind: 'deliverable',
        label: d.name,
        accountName: d.accountName,
        at: d.updatedAt,
      });
    }
  }
  for (const p of people) {
    if (p.category === 'customer') {
      for (const d of p.deliverables ?? []) {
        if (d.status === 'delivered') {
          wins.push({
            kind: 'deliverable',
            label: d.title,
            accountName: p.company ?? p.name,
            at: d.dueDate,
          });
        }
      }
    }
    if (p.category === 'prospect' && p.stage === 'closed-won') {
      const who = p.company ?? p.name;
      wins.push({
        kind: 'closed-won',
        label: `${who} signed`,
        accountName: who,
        at: p.closedAt,
      });
    }
  }
  return wins.sort((a, b) => (b.at ?? '').localeCompare(a.at ?? ''));
}

/** Highest-confidence fact in a section (optionally a specific field). */
function bestFact(
  facts: BrainFact[],
  section: BrainSection,
  field?: string,
): BrainFact | undefined {
  return facts
    .filter((f) => f.section === section && (field === undefined || f.field === field))
    .sort((a, b) => b.confidence - a.confidence)[0];
}

/** Rotate deterministically through variants — Regenerate bumps the index. */
function pick<T>(options: readonly T[], variant: number): T {
  return options[((variant % options.length) + options.length) % options.length];
}

/** Join only the lines that exist — the omit-what's-absent discipline. */
function compose(lines: Array<string | null | undefined | false>): string {
  return lines.filter((l): l is string => typeof l === 'string' && l.length > 0).join('\n\n');
}

function truncate(text: string, max: number): string {
  const t = text.trim();
  return t.length <= max ? t : `${t.slice(0, max - 1).trimEnd()}…`;
}

const brainGrounding = (f: BrainFact) =>
  `Brain · ${f.label}: ${f.value} (${f.source}, ${Math.round(f.confidence * 100)}%)`;

const normTitle = (t: string) => t.trim().toLowerCase();

/** The at-risk-turned-healthy pattern in a real activity trail. */
const RISK_RE = /at[\s-]?risk|churn risk|save plan|red account|almost churned/i;

// ---------------------------------------------------------------------------
// The generator
// ---------------------------------------------------------------------------

/**
 * Compose up to five draft specs from real context. Empty Brain → `[]`
 * (the landing falls back to the picker, honestly). Slots whose grounding
 * is absent are omitted, and any spec whose title matches an existing
 * content item (case-insensitive) is dropped — never a duplicate draft.
 */
export function preparedDrafts(
  brainFacts: BrainFact[],
  people: Person[],
  recentWins: RecentWin[],
  existingItems: ContentItem[],
  variants: Partial<Record<PreppedSlot, number>> = {},
): PreppedDraftSpec[] {
  // Empty Brain = no grounded context. Return nothing rather than inventing.
  if (brainFacts.length === 0) return [];

  const positioning = bestFact(brainFacts, 'positioning');
  const identity =
    bestFact(brainFacts, 'identity', 'brandName') ?? bestFact(brainFacts, 'identity');
  const icp = bestFact(brainFacts, 'icp');
  const goal = bestFact(brainFacts, 'goals');
  const learned = bestFact(brainFacts, 'learned');

  const win = recentWins[0];
  const persona = people.find(
    (p) => p.category === 'persona' && (p.profile?.painPoints?.length ?? 0) > 0,
  );
  const recovered = people.find(
    (p) =>
      p.category === 'customer' &&
      p.status === 'active' &&
      (p.activity ?? []).some((a) => RISK_RE.test(a.text)),
  );

  const specs: PreppedDraftSpec[] = [];

  // 1 — Customer-win post (LinkedIn): a real delivered outcome / signed doc.
  if (win) {
    const v = variants['customer-win'] ?? 0;
    specs.push({
      slot: 'customer-win',
      type: 'linkedin-post',
      title: pick(
        [
          `Customer win: ${win.label} — ${win.accountName}`,
          `Inside the ${win.accountName} win: ${win.label}`,
        ],
        v,
      ),
      body: compose([
        pick(
          [
            `${win.accountName} just got a real outcome: ${win.label}.`,
            `Shipped and landed for ${win.accountName}: ${win.label}.`,
          ],
          v,
        ),
        positioning && `Why it worked: ${positioning.value}`,
        learned && `What we keep re-learning: ${learned.value}`,
        pick(
          [
            'What outcome would move the needle for your team this quarter?',
            'What would a win like this unlock for you?',
          ],
          v,
        ),
      ]),
      grounding: [
        `Win: ${win.label} (${win.accountName})`,
        ...(positioning ? [brainGrounding(positioning)] : []),
        ...(learned ? [brainGrounding(learned)] : []),
      ],
    });
  }

  // 2 — Positioning narrative (LinkedIn): the value prop in plain words.
  if (positioning) {
    const v = variants['positioning-linkedin'] ?? 0;
    specs.push({
      slot: 'positioning-linkedin',
      type: 'linkedin-post',
      title: pick(
        [
          `In plain words: ${truncate(positioning.value, 60)}`,
          `What ${identity?.value ?? 'we'} actually sell${identity ? 's' : ''}: ${truncate(positioning.value, 48)}`,
        ],
        v,
      ),
      body: compose([
        pick(
          [
            `Strip the jargon and here is the pitch: ${positioning.value}`,
            `${positioning.value} — that is the whole pitch, no jargon.`,
          ],
          v,
        ),
        icp && `Who it is for: ${icp.value}`,
        goal && `What it is in service of: ${goal.value}`,
        pick(
          ['Does that land? Tell me where it loses you.', 'What part of that would you push back on?'],
          v,
        ),
      ]),
      grounding: [
        brainGrounding(positioning),
        ...(icp ? [brainGrounding(icp)] : []),
        ...(goal ? [brainGrounding(goal)] : []),
      ],
    });
  }

  // 3 — Single X post: one sharp, quotable line from a learned/positioning fact.
  const takeFact = learned ?? positioning ?? goal;
  if (takeFact) {
    const v = variants['x-take'] ?? 0;
    specs.push({
      slot: 'x-take',
      type: 'x-post',
      title: pick(
        [`One-liner: ${truncate(takeFact.value, 60)}`, `Hot take: ${truncate(takeFact.value, 60)}`],
        v,
      ),
      // Single post — kept under 280 characters by construction.
      body: truncate(
        pick(
          [
            `${takeFact.value}\n\nLearned the hard way, not from a listicle.`,
            `${takeFact.value}\n\nThat's the whole playbook.`,
          ],
          v,
        ),
        280,
      ),
      grounding: [brainGrounding(takeFact)],
    });
  }

  // 4 — Blog outline from a persona's REAL pain point.
  if (persona) {
    const pains = persona.profile?.painPoints ?? [];
    const v = variants['icp-pain-blog'] ?? 0;
    const pain = pick(pains, v);
    const audience = persona.profile?.role ?? persona.name;
    specs.push({
      slot: 'icp-pain-blog',
      type: 'blog',
      title: pick(
        [`Fixing "${truncate(pain, 60)}" for ${audience}`, `${audience} vs "${truncate(pain, 60)}": a field guide`],
        v,
      ),
      body: compose([
        `# ${pick([`How ${audience} teams get past: ${pain}`, `A working fix for: ${pain}`], v)}`,
        `${audience} keep hitting the same wall: ${pain}.`,
        persona.profile?.goals?.length
          ? `## What success looks like\n${persona.profile.goals.map((g) => `- ${g}`).join('\n')}`
          : null,
        positioning ? `## Where we come in\n${positioning.value}` : null,
        win ? `## Proof it works\n${win.accountName}: ${win.label}.` : null,
      ]),
      grounding: [
        `Persona: ${persona.name} — pain: ${pain}`,
        ...(positioning ? [brainGrounding(positioning)] : []),
        ...(win ? [`Win: ${win.label} (${win.accountName})`] : []),
      ],
    });
  }

  // 5 — At-risk-turned-healthy story (X thread) from a real activity trail.
  if (recovered) {
    const company = recovered.company ?? recovered.name;
    const riskNote = (recovered.activity ?? []).find((a) => RISK_RE.test(a.text));
    const v = variants['recovery-story'] ?? 0;
    specs.push({
      slot: 'recovery-story',
      type: 'x-thread',
      title: pick(
        [`From at-risk to healthy: ${company}`, `The ${company} save, step by step`],
        v,
      ),
      body: compose([
        pick(
          [
            `${company} nearly walked. Today they're one of our healthiest accounts.\n\nWhat changed:`,
            `An account we almost lost — ${company} — is now healthy.\n\nThe save, honestly:`,
          ],
          v,
        ),
        riskNote && `1/ The warning sign we logged: "${riskNote.text}"`,
        recovered.nextStep && `2/ The concrete next step that rebuilt trust: ${recovered.nextStep}.`,
        learned && `3/ The lesson we banked: ${learned.value}`,
      ]),
      grounding: [
        `Customer: ${recovered.name}${recovered.company ? ` (${recovered.company})` : ''} — active`,
        ...(riskNote ? [`Activity: ${riskNote.text}`] : []),
        ...(learned ? [brainGrounding(learned)] : []),
      ],
    });
  }

  // Never duplicate an existing draft title (or repeat one within the batch).
  const taken = new Set(existingItems.map((i) => normTitle(i.title)));
  const out: PreppedDraftSpec[] = [];
  for (const spec of specs) {
    const key = normTitle(spec.title);
    if (taken.has(key)) continue;
    taken.add(key);
    out.push(spec);
  }
  return out.slice(0, 5);
}
